import React, { useState, useEffect } from 'react';
import { fetchAvailableModels } from '../services/api';
import './AIToolkitSelector.css';

/**
 * AI Toolkit Selector Component
 * Dynamically loads and displays AI models from Admin Backend
 */
export default function AIToolkitSelector({ onContinue, onBack }) {
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Initialize from localStorage if available
  const [selectedModels, setSelectedModels] = useState(() => {
    try {
      const stored = localStorage.getItem('selectedAIModels');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Load capabilities on mount
  useEffect(() => {
    loadCapabilities();
  }, []);

  const loadCapabilities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const caps = await fetchAvailableModels();
      
      console.log(`✅ Loaded ${caps.length} capabilities from Admin Backend`);
      console.log('Capabilities:', caps.map(c => c.name));
      
      setCapabilities(caps);
      
      // Auto-select default (first) model for each capability if not already selected
      setSelectedModels(prev => {
        const updated = { ...prev };
        for (const cap of caps) {
          if (!updated[cap.key] && cap.models && cap.models.length > 0) {
            // Select the first model as default - use 'key' field (not 'id')
            const modelKey = cap.models[0].key || cap.models[0].id;
            updated[cap.key] = modelKey;
            console.log(`🔧 Auto-selected default model for ${cap.key}:`, cap.models[0].name, '→', modelKey);
          }
        }
        // Save auto-selections to localStorage
        localStorage.setItem('selectedAIModels', JSON.stringify(updated));
        console.log('💾 Saved auto-selections to localStorage:', updated);
        return updated;
      });
    } catch (err) {
      console.error('Error loading capabilities:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleModelSelect = (capabilityKey, modelKey) => {
    console.log(`🔄 handleModelSelect called: capability="${capabilityKey}", modelKey="${modelKey}"`);
    
    // Find the model name for logging
    const capability = capabilities.find(c => c.key === capabilityKey);
    const model = capability?.models?.find(m => m.key === modelKey);
    console.log(`🔄 Selected model: "${model?.name}" (key=${modelKey})`);
    
    setSelectedModels(prev => {
      const updated = {
        ...prev,
        [capabilityKey]: modelKey
      };
      // Save to localStorage immediately when model is selected
      localStorage.setItem('selectedAIModels', JSON.stringify(updated));
      console.log('✅ Model selected and saved to localStorage:', capabilityKey, '→', modelKey);
      console.log('✅ Full selectedModels object:', JSON.stringify(updated, null, 2));
      return updated;
    });
  };

  const handleContinue = () => {
    // Save selected models to localStorage
    localStorage.setItem('selectedAIModels', JSON.stringify(selectedModels));
    console.log('Selected models:', selectedModels);
    
    if (onContinue) {
      onContinue(selectedModels);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="toolkit-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p className="loading-text">Loading AI models from Admin Backend...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="toolkit-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Failed to Load AI Models</h3>
          <p className="error-message">{error}</p>
          <button onClick={loadCapabilities} className="retry-button">
            Retry
          </button>
          {onBack && (
            <button onClick={onBack} className="back-button">
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // Empty State
  if (capabilities.length === 0) {
    return (
      <div className="toolkit-container">
        <div className="empty-state">
          <p>No AI models available</p>
          <button onClick={loadCapabilities} className="retry-button">
            Refresh
          </button>
        </div>
      </div>
    );
  }

  // Main Content
  return (
    <div className="toolkit-container">
      {/* Header */}
      <div className="toolkit-header">
        {onBack && (
          <button onClick={onBack} className="back-link">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Back
          </button>
        )}
        
        <h1 className="toolkit-title">Select Your AI Toolkit</h1>
        <p className="toolkit-subtitle">
          Choose your preferred models for generation. First available model will be used if none are selected.
        </p>
      </div>

      {/* Capabilities List */}
      <div className="capabilities-list">
        {capabilities.map((capability, index) => (
          <CapabilitySection
            key={capability.key || `capability-${index}`}
            capability={capability}
            selectedModel={selectedModels[capability.key]}
            onSelectModel={(modelKey) => handleModelSelect(capability.key, modelKey)}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="toolkit-footer">
        <button onClick={handleContinue} className="continue-button">
          Continue
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * Capability Section Component
 * Displays a single capability with its models
 */
function CapabilitySection({ capability, selectedModel, onSelectModel }) {
  // Debug: Log the selected model for this capability
  console.log(`📋 Capability "${capability.name}": selectedModel=${selectedModel}`);
  console.log(`📋 Available models:`, capability.models.map(m => ({ name: m.name, key: m.key })));
  
  return (
    <div className="capability-section">
      {/* Capability Header */}
      <div className="capability-header">
        <h2 className="capability-name">{capability.name}</h2>
        <p className="capability-description">{capability.description}</p>
      </div>

      {/* Models Grid */}
      <div className="models-grid">
        {capability.models.map((model, index) => {
          const isModelSelected = selectedModel === model.key;
          // Debug: Log selection comparison for each model
          if (capability.name.includes('video') || capability.name.includes('Video')) {
            console.log(`  🎯 Model "${model.name}": key=${model.key}, isSelected=${isModelSelected}, selectedModel=${selectedModel}`);
          }
          return (
            <ModelCard
              key={model.key || `model-${index}`}
              model={model}
              isSelected={isModelSelected}
              onSelect={() => {
                console.log(`🖱️ User clicked model "${model.name}" with key=${model.key}`);
                onSelectModel(model.key);
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Model Card Component
 * Displays a single AI model
 */
function ModelCard({ model, isSelected, onSelect }) {
  return (
    <button
      onClick={onSelect}
      className={`model-card ${isSelected ? 'selected' : ''}`}
    >
      {/* Model Name */}
      <div className="model-name">{model.name}</div>

      {/* Provider */}
      {model.provider && (
        <div className="model-provider">{model.provider}</div>
      )}

      {/* Description */}
      {model.description && (
        <div className="model-description">{model.description}</div>
      )}

      {/* Cost */}
      <div className="model-cost">
        {model.cost_estimate || 'Included'}
      </div>

      {/* Selected Indicator */}
      {isSelected && (
        <div className="selected-indicator">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
          </svg>
        </div>
      )}
    </button>
  );
}
